import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getOnboarding, dismissOnboarding } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { CheckCircle2, Circle, ArrowRight, Rocket, X } from "lucide-react";
import { toast } from "sonner";

export const WelcomeChecklist = () => {
  const [ob, setOb] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    getOnboarding().then(setOb).catch(() => setOb(null));
  }, []);

  if (!ob || ob.dismissed || ob.complete) return null;
  const pct = Math.round((ob.done / ob.total) * 100);
  const next = ob.steps.find((s) => !s.done);

  const dismiss = async () => {
    await dismissOnboarding();
    setOb({ ...ob, dismissed: true });
    toast.success("Checklist hidden — bring it back any time from Settings");
  };

  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-5" data-testid="welcome-checklist">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15 text-primary"><Rocket className="h-5 w-5" /></span>
          <div>
            <div className="font-semibold" data-testid="checklist-title">Getting started with D20</div>
            <div className="text-sm text-muted-foreground">
              {next ? <>Next up: <span className="text-foreground">{next.title}</span></> : "All set!"} · <span data-testid="checklist-progress">{ob.done} of {ob.total} done</span>
            </div>
          </div>
        </div>
        {isAdmin && (
          <button data-testid="checklist-dismiss" onClick={dismiss} className="text-muted-foreground hover:text-foreground text-xs flex items-center gap-1">
            <X className="h-3.5 w-3.5" /> Hide
          </button>
        )}
      </div>
      <div className="mt-4 h-1.5 w-full rounded-full bg-muted overflow-hidden" data-testid="checklist-bar">
        <div className="h-full rounded-full bg-primary transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
        {ob.steps.map((s, i) => (
          <button key={s.key} data-testid={`checklist-step-${s.key}`} onClick={() => navigate(s.path)}
                  className={cn("group flex flex-col items-start gap-1.5 rounded-lg border p-3 text-left transition-colors",
                    s.done ? "border-emerald-500/30 bg-emerald-500/5" : "border-border bg-card hover:border-primary/50")}>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {s.done ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Circle className="h-4 w-4 text-muted-foreground/60" />}
              Step {i + 1}
            </div>
            <div className={cn("text-sm font-medium leading-tight", s.done && "line-through text-muted-foreground")}>{s.title}</div>
            <div className="text-xs text-muted-foreground leading-snug">{s.detail}</div>
            {!s.done && <span className="mt-auto text-xs text-primary flex items-center gap-1 group-hover:gap-1.5 transition-all">Go <ArrowRight className="h-3 w-3" /></span>}
          </button>
        ))}
      </div>
    </div>
  );
};
